import { createContext, PropsWithChildren, useContext, useCallback, useReducer, useState } from 'react';
import { useToggle } from 'shared/hooks';

interface State {
  contractIdForTogglingStatus: string;
}

type Action = { type: 'set-contractId-toggle-status'; payload: string };

interface ContractInventoryFormContext {
  state: State;
  newContractUploadDialogOpen: boolean;
  contractStatusDialogOpen: boolean;
  contractDeleteDialogOpen: boolean;
  contractIdToDelete: string;
  saveState: (state: State) => void;
  toggleNewContractUploadDialog: () => void;
  toggleContractStatusDialog: () => void;
  setContractIdForToggleStatus: (id: string) => void;
  toggleContractDeleteDialog: () => void;
  setContractIdToDelete: React.Dispatch<React.SetStateAction<string>>;
}

const defaultState: State = {
  contractIdForTogglingStatus: '',
};

function reducer(state: State, action: Action) {
  switch (action.type) {
    case 'set-contractId-toggle-status':
      return { ...state, contractIdForTogglingStatus: action.payload };
    default:
      return state;
  }
}

const contractInventoryFormContext = createContext({} as ContractInventoryFormContext);

export function ProvideContractUploadState({ children }: PropsWithChildren<{}>) {
  const [initialState, saveState] = useState<State>(defaultState);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [newContractUploadDialogOpen, toggleNewContractUploadDialog] = useToggle();
  const [contractStatusDialogOpen, toggleContractStatusDialog] = useToggle();
  const [contractDeleteDialogOpen, toggleContractDeleteDialog] = useToggle();
  const [contractIdToDelete, setContractIdToDelete] = useState<string>('');

  const setContractIdForToggleStatus = useCallback((id: string) => {
    dispatch({ type: 'set-contractId-toggle-status', payload: id });
  }, []);

  return (
    <contractInventoryFormContext.Provider
      value={{
        state,
        saveState,
        newContractUploadDialogOpen,
        toggleNewContractUploadDialog,
        contractStatusDialogOpen,
        toggleContractStatusDialog,
        contractDeleteDialogOpen,
        toggleContractDeleteDialog,
        setContractIdForToggleStatus,
        contractIdToDelete,
        setContractIdToDelete,
      }}
    >
      {children}
    </contractInventoryFormContext.Provider>
  );
}

export function useContractInventoryFormState() {
  return useContext(contractInventoryFormContext);
}
